/**
 * Was noch kommt: erwarteter Umsatz aus bestätigten Buchungen, erwartete Vertragskosten und die
 * Differenz. Darunter zwei Qualitätswerte zum Buchungsverhalten.
 *
 * <p>Alle Zahlen kommen unverändert vom Server. Geändert hat sich nur die Sprache: „Pipeline",
 * „Net-Forecast", „Lead-Time" und „Cancellation-Rate" waren Fachjargon aus dem Vertriebs-Englisch
 * auf einer Seite, die ein Vermieter bedient (§11).</p>
 */
import { Card, CardBody, Eyebrow } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { formatDate, formatMoney, formatNumber, formatPercent } from '@/shared/lib/format';
import type { InsightsResponse } from '@/shared/api';

interface Props {
  insights: InsightsResponse;
  currency: string;
}

export function ForecastCard({ insights, currency }: Props) {
  const forecast = insights.forecast;
  const health = insights.bookingHealth;

  return (
    <Card>
      <CardBody className="p-4 sm:p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <Eyebrow variant="muted">Ausblick</Eyebrow>
          <span className="text-ink-soft text-xs">bis {formatDate(forecast.forecastTo)}</span>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <ForecastLine
            label="Erwarteter Umsatz"
            value={formatMoney(forecast.pipelineRevenue, currency)}
            detail={`${formatNumber(forecast.pipelineBookings)} ${forecast.pipelineBookings === 1 ? 'Buchung' : 'Buchungen'} · davon ${formatMoney(forecast.pipelineRevenueNext30Days, currency)} in den nächsten 30 Tagen${reviewDetail(forecast.priceUnderReviewCount)}`}
            tone="positive"
          />
          <ForecastLine
            label="Erwartete Fixkosten"
            value={formatMoney(forecast.expectedRecurringCosts, currency)}
            detail="Laufende Verträge für die verbleibenden Monate"
            // Eine Null ist keine schlechte Nachricht — in Sealwax las sie sich wie eine.
            tone={forecast.expectedRecurringCosts > 0 ? 'negative' : 'neutral'}
          />
          <ForecastLine
            label="Erwartetes Ergebnis"
            value={formatMoney(forecast.netForecast, currency)}
            detail="Erwarteter Umsatz minus Fixkosten"
            tone={forecast.netForecast >= 0 ? 'positive' : 'negative'}
          />
        </div>

        <dl className="border-line/60 mt-6 grid gap-3 border-t pt-4 text-sm sm:grid-cols-2">
          <HealthLine
            label="Vorlauf bis zur Anreise"
            value={
              health.leadTimeDays != null
                ? `${formatNumber(Math.round(health.leadTimeDays))} Tage`
                : 'noch keine Buchung'
            }
            detail="im Durchschnitt"
          />
          <HealthLine
            label="Stornoquote"
            value={
              health.cancellationRate != null
                ? formatPercent(health.cancellationRate * 100)
                : 'noch keine Buchung'
            }
            detail={`${formatNumber(health.cancelledBookingCount)} von ${formatNumber(health.totalBookingCount)} Buchungen`}
          />
        </dl>
      </CardBody>
    </Card>
  );
}

function reviewDetail(count: number) {
  return count === 0
    ? ''
    : ` · ${count} Preis${count === 1 ? '' : 'e'} wird${count === 1 ? '' : 'en'} geprüft`;
}

const TONE_CLASS = {
  positive: 'text-sage',
  negative: 'text-sealwax',
  neutral: 'text-ink',
} as const;

function ForecastLine({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  tone: keyof typeof TONE_CLASS;
}) {
  return (
    <div className="min-w-0">
      <div className="text-ink-soft text-[11px] font-semibold uppercase tracking-[0.12em]">
        {label}
      </div>
      <div className={cn('mt-2 font-serif text-2xl tabular-nums', TONE_CLASS[tone])}>{value}</div>
      <div className="text-ink-soft mt-1 text-xs">{detail}</div>
    </div>
  );
}

function HealthLine({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-soft text-xs uppercase tracking-wider">{label}</dt>
      <dd className="min-w-0 text-right">
        <span className="text-ink block tabular-nums">{value}</span>
        {detail && <span className="text-ink-faint block text-[11px]">{detail}</span>}
      </dd>
    </div>
  );
}
